// Règles d'action sur un RDV qualifié côté agence. Les RDV viennent du partenaire
// (champs `statut`, `slot.start`) ou, en secours, de la table locale (`status`,
// `scheduled_at`) : on accepte les deux formes.

type RdvLike = {
  statut?: string;
  status?: string;
  slot?: { start?: string | null; end?: string | null } | null;
  slot_start?: string | null;
  slot_end?: string | null;
  scheduled_at?: string | null;
};

// Le partenaire n'accepte l'annulation que sur un RDV confirmé (décalé : 400 invalid_statut).
// Un RDV décalé reçoit une nouvelle date, puis il est honoré ou passe en no-show.
const CANCELLABLE_STATUSES = ['confirmed'];
const RESCHEDULABLE_STATUSES = ['confirmed', 'postponed'];
const RESCHEDULE_AFTER_COMPLETION_MS = 48 * 60 * 60 * 1000;

const rdvStatus = (rdv: RdvLike): string | undefined => rdv.statut ?? rdv.status;

export const rdvStartDate = (rdv: RdvLike): Date | null => {
  const raw = rdv.slot?.start ?? rdv.slot_start ?? rdv.scheduled_at;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Le partenaire refuse (400) l'annulation d'un créneau écoulé : un RDV passé se
// déclare en no-show. Sans date connue, on laisse le partenaire trancher.
export const canCancelRdv = (rdv: RdvLike, now: Date = new Date()): boolean => {
  const status = rdvStatus(rdv);
  if (!status || !CANCELLABLE_STATUSES.includes(status)) return false;
  const start = rdvStartDate(rdv);
  const isUpcoming = start === null || start.getTime() >= now.getTime();
  return isUpcoming;
};

// Mêmes règles que le portail partenaire : RDV confirmé ou déjà décalé, ou honoré
// depuis moins de 48 h (comptées depuis la fin du créneau, à défaut son début).
export const canRescheduleRdv = (rdv: RdvLike, now: Date = new Date()): boolean => {
  const status = rdvStatus(rdv);
  if (status && RESCHEDULABLE_STATUSES.includes(status)) return true;
  if (status !== 'completed') return false;
  const rawEnd = rdv.slot?.end ?? rdv.slot_end;
  const end = rawEnd ? new Date(rawEnd) : rdvStartDate(rdv);
  if (!end || Number.isNaN(end.getTime())) return false;
  return now.getTime() - end.getTime() <= RESCHEDULE_AFTER_COMPLETION_MS;
};

export const isPostponedRdv = (rdv: RdvLike): boolean => rdvStatus(rdv) === 'postponed';

export const canReportNoShow = (rdv: RdvLike): boolean => rdvStatus(rdv) === 'completed';

// Codes d'erreur connus du partenaire, traduits pour l'agence.
const PARTNER_ERROR_MESSAGES: Record<string, string> = {
  slot_unavailable: "Ce créneau n'est pas disponible (agenda déjà pris ou temps de trajet insuffisant).",
  invalid_slot: 'Date invalide ou déjà passée.',
  invalid_statut: 'Ce rendez-vous ne peut plus être modifié dans son état actuel.',
  reschedule_window_closed: 'Ce rendez-vous a été honoré il y a plus de 48 h : il ne peut plus être décalé.',
  rdv_not_owned_by_agent: "Ce rendez-vous n'appartient pas à votre agence.",
  agent_belongs_to_another_partner: "Votre agence n'est pas rattachée à Skipcar chez le partenaire.",
  rdv_not_found: 'Rendez-vous introuvable.',
  agent_not_found: 'Agence introuvable chez le partenaire.',
  new_slot_required: 'Choisissez la nouvelle date du rendez-vous.',
  motif_required: "Indiquez le motif de l'annulation.",
};

// Le backend relaie tel quel le motif du partenaire (champ `message`, même code HTTP).
export const describeRdvActionError = (error: unknown): string => {
  const err = error as { response?: { status?: number; data?: { message?: unknown } } } | null;
  const message = err?.response?.data?.message;
  if (typeof message === 'string' && PARTNER_ERROR_MESSAGES[message]) return PARTNER_ERROR_MESSAGES[message];
  if (typeof message === 'string' && message.trim()) return message;
  if (err?.response?.status === 400) {
    return "Le partenaire a refusé l'annulation. Si le créneau est déjà passé, déclarez plutôt un no-show.";
  }
  return 'Une erreur est survenue, réessayez.';
};
